import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ROLE_COURT_TYPE, ROLE_BADGE } from "./roleConstants";

const normName = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, " ").trim().toLowerCase();

// Επιλογή Τομέων (Υπεύθυνος Τομέα) ή Τομέα → Σύνθεσης (Πρόεδρος) — value: {sector_numbers | composition_sector_number, composition_label}
export const ScopeEditor = ({ role, fullName, value, onChange, tid = "role-scope-fields" }) => {
  const ct = ROLE_COURT_TYPE[role];
  const president = role === "composition_president";
  const [sectors, setSectors] = useState([]);
  const v = value || {};

  useEffect(() => {
    if (!ct) return;
    axios.get(`${API}/pretrial-sectors`, { params: { court_type: ct } }).then(r => setSectors(r.data?.sectors || [])).catch(() => setSectors([]));
  }, [ct]);

  const proposal = useMemo(() => {
    if (!president || !fullName) return null;
    const me = normName(fullName);
    for (const s of sectors) {
      for (const c of (s.compositions || [])) {
        const ms = (c.members || []).filter(m => m?.name);
        const hit = ms.find(m => m.role === "president" && normName(m.name) === me) || ms.find(m => normName(m.name) === me);
        if (hit) return { sector_number: s.sector_number, label: c.label };
      }
    }
    return null;
  }, [president, fullName, sectors]);

  useEffect(() => {
    if (proposal && !v.composition_label) onChange({ ...v, composition_sector_number: proposal.sector_number, composition_label: proposal.label });
  }, [proposal]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!ct) return null;
  const sector = sectors.find(s => s.sector_number === v.composition_sector_number);
  const primary = tid === "role-scope-fields";
  const ids = {
    proposal: primary ? "composition-proposal" : `${tid}-proposal`,
    sector: primary ? "composition-sector-select" : `${tid}-sector-select`,
    label: primary ? "composition-label-select" : `${tid}-label-select`,
    check: (n) => (primary ? `role-sector-${n}-checkbox` : `${tid}-sector-${n}-checkbox`),
  };

  if (president) {
    return (
      <div className="space-y-2 p-3 bg-violet-50 rounded-lg border border-violet-200" data-testid={tid}>
        <Label>Τομέας → Σύνθεση Πολυμελούς *</Label>
        {proposal && <p className="text-xs text-violet-800" data-testid={ids.proposal}>Πρόταση από ονοματεπώνυμο: {proposal.sector_number}ος Τομέας → «{proposal.label}»</p>}
        <div className="grid grid-cols-2 gap-2">
          <Select value={v.composition_sector_number ? String(v.composition_sector_number) : ""}
                  onValueChange={x => onChange({ ...v, composition_sector_number: Number(x), composition_label: "" })}>
            <SelectTrigger data-testid={ids.sector}><SelectValue placeholder="Τομέας…" /></SelectTrigger>
            <SelectContent>
              {sectors.map(s => <SelectItem key={s.sector_number} value={String(s.sector_number)}>{s.sector_number}ος{s.title ? ` — ${s.title}` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={v.composition_label || ""} onValueChange={x => onChange({ ...v, composition_label: x })} disabled={!sector}>
            <SelectTrigger data-testid={ids.label}><SelectValue placeholder="Σύνθεση…" /></SelectTrigger>
            <SelectContent>
              {(sector?.compositions || []).filter(c => (c.label || "").trim()).map((c, i) => (
                <SelectItem key={i} value={c.label}>{c.label} — {(c.members || []).filter(m => m?.name).map(m => m.name).join(", ")}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2 p-3 bg-violet-50 rounded-lg border border-violet-200" data-testid={tid}>
      <Label>Τομείς {ct} (ένας ή περισσότεροι) *</Label>
      {sectors.map(s => (
        <div key={s.sector_number} className="flex items-start space-x-2">
          <Checkbox id={`${tid}-sec-${s.sector_number}`} checked={(v.sector_numbers || []).includes(s.sector_number)}
                    onCheckedChange={checked => {
                      const cur = v.sector_numbers || [];
                      onChange({ ...v, sector_numbers: checked ? [...cur, s.sector_number].sort() : cur.filter(n => n !== s.sector_number) });
                    }} data-testid={ids.check(s.sector_number)} />
          <label htmlFor={`${tid}-sec-${s.sector_number}`} className="text-sm leading-tight">
            {s.sector_number}ος Τομέας{s.title ? ` — ${s.title}` : ""}
            <span className="block text-xs text-slate-500">{(s.pinakio_labels || []).join(", ") || "χωρίς πινάκια"}</span>
          </label>
        </div>
      ))}
    </div>
  );
};

// Κύριος ρόλος → πεδία στο ίδιο το form (sector_numbers / composition_*)
export const RoleScopeFields = ({ form, setForm }) => (
  <ScopeEditor role={form.role} fullName={form.full_name}
               value={{ sector_numbers: form.sector_numbers, composition_sector_number: form.composition_sector_number, composition_label: form.composition_label }}
               onChange={(v) => setForm(f => ({ ...f, ...v }))} />
);

const ALL_ROLES = ["user", "judge", "dispatch", "katastima", "sector_head_poly", "sector_head_mono", "composition_president"];

// iter94: επιπλέον ιδιότητες (ο χρήστης επιλέγει προφίλ στη σύνδεση) — ρυθμίσεις ανά ρόλο στο role_config[role]
export const ExtraRolesFields = ({ form, setForm }) => {
  const extra = (form.roles || []).filter(r => r !== form.role);
  const toggle = (r, on) => setForm(f => ({ ...f, roles: on ? [...(f.roles || []).filter(x => x !== r), r] : (f.roles || []).filter(x => x !== r) }));
  return (
    <div className="space-y-2" data-testid="extra-roles-fields">
      <Label>Επιπλέον ιδιότητες <span className="text-xs text-slate-400">(ο χρήστης επιλέγει ιδιότητα κατά τη σύνδεση)</span></Label>
      <div className="grid grid-cols-2 gap-1 p-3 bg-slate-50 rounded-lg">
        {ALL_ROLES.filter(r => r !== form.role).map(r => (
          <div key={r} className="flex items-center space-x-2">
            <Checkbox id={`xrole-${r}`} checked={extra.includes(r)} onCheckedChange={c => toggle(r, !!c)} data-testid={`extra-role-${r}-checkbox`} />
            <label htmlFor={`xrole-${r}`} className="text-xs">{ROLE_BADGE[r] || "Χρήστης"}</label>
          </div>
        ))}
      </div>
      {extra.filter(r => ROLE_COURT_TYPE[r]).map(r => (
        <div key={r} className="space-y-1">
          <p className="text-xs font-medium text-slate-700">Ρυθμίσεις για «{ROLE_BADGE[r]}»</p>
          <ScopeEditor role={r} fullName={form.full_name} value={(form.role_config || {})[r]} tid={`extra-scope-${r}`}
                       onChange={(v) => setForm(f => ({ ...f, role_config: { ...(f.role_config || {}), [r]: v } }))} />
        </div>
      ))}
    </div>
  );
};

export default RoleScopeFields;
